import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    useFloating,
    offset,
    shift,
    flip,
    autoUpdate,
    useInteractions,
    useHover,
    useClick,
    useDismiss,
    useRole,
    safePolygon,
    FloatingFocusManager,
} from '@floating-ui/react';
import logo from "../../img/inroads-logo-314.png";
import './Nav.css';

const DropdownMenuItem = ({ item }) => {
    const [isOpen, setIsOpen] = useState(false);

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: 'bottom-start',
        middleware: [offset(5), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });

    // Hover with safePolygon: cursor can travel from the button into the dropdown
    // through the gap without closing. restMs prevents accidental opens while
    // sweeping across the menu bar; close delay gives a forgiveness window so
    // a quick re-hover doesn't flicker.
    const hover = useHover(context, {
        handleClose: safePolygon({ blockPointerEvents: false }),
        restMs: 40,
        delay: { close: 120 },
    });
    // Click coexists with hover: tapping toggles, and on touch devices where
    // hover is unreliable, click is the primary opener. Floating-ui suppresses
    // hover-reopen until the pointer leaves and re-enters, so click-to-close
    // doesn't immediately get reopened by the still-hovering cursor.
    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'menu' });

    const { getReferenceProps, getFloatingProps } = useInteractions([
        hover,
        click,
        dismiss,
        role,
    ]);

    return (
        <div className="menu-item">
            <button
                ref={refs.setReference}
                className="nav-link"
                aria-haspopup="menu"
                aria-expanded={isOpen}
                {...getReferenceProps()}
            >
                {item.name}
            </button>
            {isOpen && (
                <FloatingFocusManager context={context} modal={false} initialFocus={-1}>
                    <div
                        ref={refs.setFloating}
                        className="dropdown-menu"
                        style={floatingStyles}
                        {...getFloatingProps()}
                    >
                        {item.submenu.map((sub, i) => (
                            <Link
                                key={i}
                                to={sub.link}
                                className="dropdown-item"
                                role="menuitem"
                                onClick={() => setIsOpen(false)}
                            >
                                {sub.name}
                            </Link>
                        ))}
                    </div>
                </FloatingFocusManager>
            )}
        </div>
    );
};

const Nav = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const menuItems = [
        { name: 'Home', link: '/' },
        {
            name: 'About Us',
            link: '/about',
            submenu: [
                { name: 'Our Story', link: '/Our-Story' },
                { name: 'Our Team', link: '/Our-Team' },
                { name: 'FAQ', link: '/Frequently-Asked-Questions' },
                { name: 'Testimonials', link: '/Testimonials' }
            ]
        },
        {
            name: 'Careers',
            link: 'https://inroadstorecovery.hireclick.com/jobboard',
            isExternal: true,
            newTab: false,
        },
        { name: 'Services', link: '/services' },
        { name: 'Residential Program', link: '/services/Residential-Program' },
        { name: 'Contact', link: './Contact' },
        { name: 'Community Partnerships', link: '/Community-Partnerships' },
        { name: 'Inroads to Technology', link: '/inroads-to-technology' },
        {
            name: 'Patient Portal',
            link: 'https://patientonlineportal.com/idp/account/signin',
            isExternal: true,
            isCTA: true
        }
    ];

    // Flatten menu items for mobile view
    const flattenedMobileItems = menuItems.reduce((acc, item) => {
        if (item.submenu) {
            return [...acc, ...item.submenu];
        }
        return [...acc, item];
    }, []);

    return (
        <nav className="nav-container">
            <div className="nav-content">
                <Link to="/" className="logo">
                    <img src={logo} alt="Inroads to Recovery" />
                </Link>

                {/* Desktop Navigation */}
                <div className="desktop-menu">
                    {menuItems.map((item, index) => (
                        item.submenu ? (
                            <DropdownMenuItem key={index} item={item} />
                        ) : (
                            <div key={index} className="menu-item">
                                {item.isExternal ? (
                                    <a
                                        href={item.link}
                                        target={item.newTab === false ? undefined : '_blank'}
                                        rel={item.newTab === false ? undefined : 'noreferrer'}
                                        className={`nav-link${item.isCTA ? ' nav-cta' : ''}`}
                                    >
                                        {item.name}
                                    </a>
                                ) : (
                                    <Link to={item.link} className={`nav-link${item.isCTA ? ' nav-cta' : ''}`}>
                                        {item.name}
                                    </Link>
                                )}
                            </div>
                        )
                    ))}
                </div>

                {/* Mobile Menu Button */}
                <button
                    className="mobile-menu-button"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                    ☰
                </button>

                {/* Mobile Navigation - Flattened */}
                <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
                    {flattenedMobileItems.map((item, index) => (
                        <div key={index} className="mobile-menu-item">
                            {item.isExternal ? (
                                <a
                                    href={item.link}
                                    target={item.newTab === false ? undefined : '_blank'}
                                    rel={item.newTab === false ? undefined : 'noreferrer'}
                                    className={`mobile-nav-link${item.isCTA ? ' mobile-nav-cta' : ''}`}
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {item.name}
                                </a>
                            ) : (
                                <Link
                                    to={item.link}
                                    className="mobile-nav-link"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {item.name}
                                </Link>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </nav>
    );
};

export default Nav;